/**
 * Utility functions untuk parsing dan memproses dokumen Word
 */

/**
 * Interface untuk variable dengan informasi posisi
 */
export interface VariableInfo {
  name: string;
  position: number; // Posisi dalam dokumen (index)
  group_id?: number | null; // ID group yang bertanggung jawab untuk variable ini
}

/**
 * Extract semua variable dari teks yang memiliki format {variable_name}
 * @param text - Teks yang akan di-parse
 * @returns Array of variable info dengan posisi, diurutkan berdasarkan urutan muncul
 */
export function extractVariables(text: string): VariableInfo[] {
  const regex = /\{([^}]+)\}/g;
  const variables: VariableInfo[] = [];
  const seenVariables = new Map<string, number>(); // Map untuk track variable pertama kali muncul
  let match;

  while ((match = regex.exec(text)) !== null) {
    const variableName = match[1].trim();
    if (variableName && !seenVariables.has(variableName)) {
      seenVariables.set(variableName, match.index);
      variables.push({
        name: variableName,
        position: match.index
      });
    }
  }

  // Sort berdasarkan posisi dalam dokumen
  return variables.sort((a, b) => a.position - b.position);
}

/**
 * Replace variable dalam teks dengan nilai yang diberikan
 * @param text - Teks yang akan di-replace
 * @param variables - Object dengan key sebagai nama variable dan value sebagai nilai pengganti
 * @returns Teks dengan variable yang sudah di-replace
 */
export function replaceVariables(text: string, variables: Record<string, string>): string {
  let result = text;
  
  Object.keys(variables).forEach(key => {
    const regex = new RegExp(`\\{${key}\\}`, 'g');
    result = result.replace(regex, variables[key] || `{${key}}`);
  });

  return result;
}

/**
 * Replace variable dalam HTML content
 * @param html - HTML content yang akan di-replace
 * @param variables - Object dengan key sebagai nama variable dan value sebagai nilai pengganti
 * @returns HTML dengan variable yang sudah di-replace
 */
export function replaceVariablesInHTML(html: string, variables: Record<string, string>): string {
  let result = html;
  const processedKeys = new Set<string>();
  
  Object.keys(variables).forEach(key => {
    // Skip jika key sudah diproses (case-insensitive)
    const lowerKey = key.toLowerCase();
    if (processedKeys.has(lowerKey)) {
      return;
    }
    processedKeys.add(lowerKey);
    
    const value = variables[key] || `{${key}}`;
    // Escape HTML untuk keamanan (hanya karakter berbahaya)
    const escapedValue = value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Untuk case-insensitive matching yang lebih reliable, kita buat pattern yang match semua variasi case
    // Contoh: untuk "no", kita perlu match {no}, {No}, {NO}, {nO}
    // Kita akan menggunakan character class [nN][oO] untuk match semua variasi
    
    // Convert key ke pattern yang match semua variasi case
    const caseInsensitivePattern = key
      .split('')
      .map(char => {
        if (/[a-zA-Z]/.test(char)) {
          // Untuk huruf, buat character class [aA] untuk match semua case
          return `[${char.toLowerCase()}${char.toUpperCase()}]`;
        } else {
          // Untuk karakter non-huruf (seperti underscore, angka), escape jika perlu
          return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }
      })
      .join('');
    
    // Buat regex dengan pattern yang sudah handle semua variasi case
    const pattern = `\\{${caseInsensitivePattern}\\}`;
    const regex = new RegExp(pattern, 'g'); // Tidak perlu 'i' flag karena sudah handle di pattern
    
    // Check if pattern exists in HTML before replacing
    const matches = result.match(regex);
    if (matches && matches.length > 0) {
      result = result.replace(regex, escapedValue);
      console.log(`✅ Replaced ${matches.length} occurrence(s) of {${key}} (all case variations: {${key.toLowerCase()}}, {${key.toUpperCase()}}, etc.) with: "${escapedValue}"`);
    } else {
      // Log jika pattern tidak ditemukan untuk debugging
      console.log(`❌ Pattern {${key}} not found in HTML (checked all case variations: {${key.toLowerCase()}}, {${key.toUpperCase()}}, etc.)`);
    }
  });

  // Log jika ada variable yang tidak ter-replace
  const unmatchedVars = result.match(/\{[A-Za-z_]+[A-Za-z0-9_]*\}/g);
  if (unmatchedVars && unmatchedVars.length > 0) {
    const uniqueUnmatched = Array.from(new Set(unmatchedVars));
    console.warn('⚠️ Variables not replaced:', uniqueUnmatched);
  } else {
    console.log('🎉 All variables replaced successfully!');
  }

  return result;
}

